import React, { useState, useEffect } from 'react';
import Sidebar from '../Includes/Sidebar';
import Footer from '../Includes/Footer';
import { useFormik } from 'formik';
import { toast, ToastContainer } from 'react-toastify';

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.azmrehabcentre.pk';
const SESSION_ID = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper function to get admin ID safely
const getAdminId = () => {
    try {
        return localStorage.getItem('admin_id') || 'admin_001';
    } catch (error) {
        console.warn('localStorage access failed:', error);
        return 'admin_001';
    }
};

// API service for patient tokens with better error handling
const PatientTokenService = {
    baseURL: `${API_BASE_URL}/patient-tokens`,
    
    async create(data) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

            const response = await fetch(this.baseURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(data),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = `HTTP error! status: ${response.status}`;
                
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (e) {
                    errorMessage = errorText || errorMessage;
                }
                
                throw new Error(errorMessage);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error('Server returned invalid response format');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error creating patient token:', error);
            
            if (error.name === 'AbortError') {
                return { success: false, message: 'Request timeout - Server took too long to respond' };
            }
            
            if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                return { success: false, message: 'Network error - Please check your internet connection' };
            }
            
            return { success: false, message: error.message };
        }
    },
    
    async getNextToken(prefix = 'A', recordDate = null) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

            const params = new URLSearchParams({
                prefix,
                ...(recordDate && { record_date: recordDate })
            });
            
            const response = await fetch(`${this.baseURL}/next-token?${params}`, {
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error('Server returned invalid response format');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error getting next token:', error);
            
            if (error.name === 'AbortError') {
                return { success: false, message: 'Request timeout' };
            }
            
            if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                return { success: false, message: 'Network error - Unable to connect to server' };
            }
            
            return { success: false, message: error.message };
        }
    },
    
    async markAsPrinted(id) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await fetch(`${this.baseURL}/${id}/mark-printed`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error('Server returned invalid response format');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error marking as printed:', error);
            return { success: false, message: error.message };
        }
    },
    
    async getDailyStats(date = null) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const params = new URLSearchParams({
                ...(date && { date })
            });
            
            const response = await fetch(`${this.baseURL}/stats/daily?${params}`, {
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error('Server returned invalid response format');
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error('Error getting daily stats:', error);
            return { success: false, message: error.message };
        }
    }
};

// Token history service with better error handling
const TokenHistoryService = {
    async saveToken(token, type = 'called', voiceSettings = {}) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await fetch(`${API_BASE_URL}/token-history`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    token: token,
                    type: type,
                    called_at: new Date().toISOString(),
                    session_id: SESSION_ID,
                    admin_id: getAdminId(),
                    metadata: {
                        voice_settings: voiceSettings,
                        browser: navigator.userAgent
                    }
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                throw new Error('Server returned invalid response format');
            }

            const data = await response.json();
            console.log('Token saved to database:', data);
            return data;
        } catch (error) {
            console.error('Error saving token to database:', error);
            // toast.error('ڈیٹابیس میں محفوظ کرنے میں خرابی');
            return null;
        }
    }
};

export default function PatientReceipt() {
    const [isLoading, setIsLoading] = useState(false);
    const [tokenNumber, setTokenNumber] = useState('');
    const [services, setServices] = useState([
        { id: 1, serviceName: 'Consultation Fee', price: 500 }
    ]);
    const [defaultService, setDefaultService] = useState({
        name: 'Consultation Fee',
        price: 500
    });
    const [dailyStats, setDailyStats] = useState(null);
    const [voiceSettings, setVoiceSettings] = useState({
        enabled: false,
        language: 'ur-PK',
        voice: 'default'
    });

    // Helper function to safely access localStorage
    const safeLocalStorage = {
        getItem: (key) => {
            try {
                return localStorage.getItem(key);
            } catch (error) {
                console.warn('localStorage getItem failed:', error);
                return null;
            }
        },
        setItem: (key, value) => {
            try {
                localStorage.setItem(key, value);
            } catch (error) {
                console.warn('localStorage setItem failed:', error);
            }
        }
    };

    // Calculate total amount function
    const calculateTotal = (servicesList) => {
        const total = servicesList.reduce((sum, service) => {
            return sum + (parseFloat(service.price) || 0);
        }, 0);
        return total;
    };

    // Token initialization with better error handling
    useEffect(() => {
        const initializeToken = async () => {
            try {
                const today = new Date().toISOString().split('T')[0];
                const savedTokenDate = safeLocalStorage.getItem('currentTokenDate');
                
                // Always fetch next token from API for current date
                const response = await PatientTokenService.getNextToken('A', today);
                
                if (response && response.success && response.data) {
                    const nextToken = response.data.next_token;
                    setTokenNumber(nextToken);
                    safeLocalStorage.setItem('currentTokenNumber', nextToken);
                    safeLocalStorage.setItem('currentTokenDate', today);
                } else {
                    // Fallback logic
                    if (savedTokenDate === today) {
                        const savedToken = safeLocalStorage.getItem('currentTokenNumber');
                        setTokenNumber(savedToken || 'A001');
                    } else {
                        // New day - start from A001
                        setTokenNumber('A001');
                        safeLocalStorage.setItem('currentTokenNumber', 'A001');
                        safeLocalStorage.setItem('currentTokenDate', today);
                    }
                    
                    if (response && !response.success) {
                        toast.warn(`Token API error: ${response.message}. Using fallback token.`);
                    }
                }
            } catch (error) {
                console.error('Error initializing token:', error);
                toast.error('Failed to initialize token. Using fallback.');
                
                // Fallback to A001 for new day
                const today = new Date().toISOString().split('T')[0];
                setTokenNumber('A001');
                safeLocalStorage.setItem('currentTokenNumber', 'A001');
                safeLocalStorage.setItem('currentTokenDate', today);
            }
        };

        initializeToken();
    }, []);

    // Load daily stats with error handling
    useEffect(() => {
        const loadDailyStats = async () => {
            try {
                const response = await PatientTokenService.getDailyStats();
                if (response && response.success && response.data) {
                    setDailyStats(response.data);
                } else if (response && !response.success) {
                    console.warn('Failed to load daily stats:', response.message);
                    // toast.warn('Unable to load daily statistics');
                }
            } catch (error) {
                console.error('Error loading daily stats:', error);
                toast.error('Failed to load daily statistics');
            }
        };

        loadDailyStats();
    }, []);

    // Load default service from localStorage
    useEffect(() => {
        const savedDefault = safeLocalStorage.getItem('defaultService');
        if (savedDefault) {
            try {
                const parsed = JSON.parse(savedDefault);
                setDefaultService(parsed);
                
                // Update first service if it's still default
                if (services.length === 1 && services[0].serviceName === 'Consultation Fee') {
                    const updatedServices = [{ id: 1, serviceName: parsed.name, price: parsed.price }];
                    setServices(updatedServices);
                }
            } catch (e) {
                console.error('Error loading default service:', e);
                toast.error('Failed to load default service settings');
            }
        }
    }, []);

    // Load voice settings
    useEffect(() => {
        const savedVoiceSettings = safeLocalStorage.getItem('voiceSettings');
        if (savedVoiceSettings) {
            try {
                setVoiceSettings(JSON.parse(savedVoiceSettings));
            } catch (e) {
                console.error('Error loading voice settings:', e);
            }
        }
    }, []);

    const { values, errors, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm } = useFormik({
        initialValues: {
            patientName: '',
            doctorName: '',
            appointmentDate: new Date().toISOString().split('T')[0],
            appointmentTime: new Date().toTimeString().slice(0, 5),
            phone: '',
            address: '',
            totalAmount: calculateTotal([{ id: 1, serviceName: 'Consultation Fee', price: 500 }])
        },
        validate: (values) => {
            const errors = {};
            
            if (!values.patientName) {
                errors.patientName = "Patient name is required";
            } else if (values.patientName.length > 255) {
                errors.patientName = "Patient name must be less than 255 characters";
            }
            
            if (values.doctorName && values.doctorName.length > 255) {
                errors.doctorName = "Doctor name must be less than 255 characters";
            }
            
            if (!values.phone) {
                errors.phone = "Phone number is required";
            } else if (!/^(\+92|0)?[0-9]{10}$/.test(values.phone.replace(/[-\s]/g, ''))) {
                errors.phone = "Please enter a valid phone number";
            }
            
            if (!values.totalAmount || values.totalAmount <= 0) {
                errors.totalAmount = "Total amount must be greater than 0";
            }
            
            return errors;
        },
        onSubmit: async (values, action) => {
            // Validation checks
            if (!tokenNumber.trim()) {
                toast.error("Please enter a token number");
                return;
            }

            // Check if token format is valid
            if (!/^[A-Z]\d{3}$/.test(tokenNumber.trim())) {
                toast.error("Token format should be like A001, B001, etc.");
                return;
            }

            if (services.length === 0 || !services[0].serviceName.trim()) {
                toast.error("Please add at least one service");
                return;
            }

            setIsLoading(true);
            
            try {
                // Prepare receipt data for API
                const receiptData = {
                    token_number: tokenNumber.trim(),
                    patient_name: values.patientName.trim(),
                    phone: values.phone.trim(),
                    doctor_name: values.doctorName.trim(),
                    address: values.address.trim(),
                    appointment_date: values.appointmentDate,
                    appointment_time: values.appointmentTime,
                    total_amount: parseFloat(values.totalAmount),
                    services: services.filter(service => service.serviceName.trim() !== ''),
                    record_date: new Date().toISOString().split('T')[0]
                };

                console.log('Sending receipt data:', receiptData);

                // Create receipt via API
                const response = await PatientTokenService.create(receiptData);
                
                console.log('API Response:', response);

                if (response && response.success) {
                    toast.success("Receipt created successfully");
                    
                    // Save token to history
                    await TokenHistoryService.saveToken(tokenNumber, 'created', voiceSettings);
                    
                    // Print receipt after successful creation
                    handlePrintReceiptWithData({
                        ...values,
                        tokenNumber: tokenNumber,
                        services: services.filter(service => service.serviceName.trim() !== '')
                    });
                    
                    // Mark as printed if API call was successful
                    if (response.data && response.data.id) {
                        await PatientTokenService.markAsPrinted(response.data.id);
                    }
                    
                    // Get next token from API for same day
                    const today = new Date().toISOString().split('T')[0];
                    const nextTokenResponse = await PatientTokenService.getNextToken('A', today);
                    
                    if (nextTokenResponse && nextTokenResponse.success && nextTokenResponse.data) {
                        const nextToken = nextTokenResponse.data.next_token;
                        setTokenNumber(nextToken);
                        safeLocalStorage.setItem('currentTokenNumber', nextToken);
                        safeLocalStorage.setItem('currentTokenDate', today);
                    } else {
                        // Fallback increment if API fails
                        const nextToken = await getNextTokenNumber(tokenNumber);
                        setTokenNumber(nextToken);
                        safeLocalStorage.setItem('currentTokenNumber', nextToken);
                        
                        if (nextTokenResponse && !nextTokenResponse.success) {
                            toast.warn('Unable to fetch next token from server. Using local increment.');
                        }
                    }
                    
                    // Reset form with current date/time and proper total
                    const resetServices = [{ id: 1, serviceName: defaultService.name, price: defaultService.price }];
                    action.resetForm({
                        values: {
                            patientName: '',
                            doctorName: '',
                            appointmentDate: new Date().toISOString().split('T')[0],
                            appointmentTime: new Date().toTimeString().slice(0, 5),
                            phone: '',
                            address: '',
                            totalAmount: calculateTotal(resetServices)
                        }
                    });
                    setServices(resetServices);
                    
                    // Refresh daily stats
                    const statsResponse = await PatientTokenService.getDailyStats();
                    if (statsResponse && statsResponse.success && statsResponse.data) {
                        setDailyStats(statsResponse.data);
                    }
                } else {
                    // Handle different types of errors
                    if (response?.message) {
                        if (response.message.toLowerCase().includes('already exists') || 
                            response.message.toLowerCase().includes('duplicate')) {
                            toast.error(`Token ${tokenNumber} already exists for today!`);
                            
                            // Auto-fetch next available token
                            const today = new Date().toISOString().split('T')[0];
                            const nextTokenResponse = await PatientTokenService.getNextToken('A', today);
                            
                            if (nextTokenResponse && nextTokenResponse.success && nextTokenResponse.data) {
                                const nextToken = nextTokenResponse.data.next_token;
                                setTokenNumber(nextToken);
                                safeLocalStorage.setItem('currentTokenNumber', nextToken);
                                toast.info(`Next available token: ${nextToken}`);
                            } else {
                                toast.error('Unable to fetch next available token. Please manually change the token number.');
                            }
                        } else if (response.message.toLowerCase().includes('network') || 
                                   response.message.toLowerCase().includes('timeout') ||
                                   response.message.toLowerCase().includes('connection')) {
                            toast.error('Network error - Please check your internet connection and try again');
                        } else {
                            toast.error(`Error: ${response.message}`);
                        }
                    } else {
                        toast.error("Unknown error occurred while creating receipt");
                    }
                    console.error('API Error:', response);
                }
                
            } catch (err) {
                console.error("Error creating receipt:", err);
                
                if (err.name === 'AbortError') {
                    toast.error("Request timeout - Server took too long to respond");
                } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                    toast.error("Network error - Unable to connect to server. Please check your internet connection.");
                } else {
                    toast.error("An unexpected error occurred. Please try again.");
                }
            } finally {
                setIsLoading(false);
            }
        }
    });

    // Function to get next token number (with API integration)
    const getNextTokenNumber = async (currentToken) => {
        try {
            const prefix = currentToken.replace(/\d+$/, '');
            const response = await PatientTokenService.getNextToken(prefix);
            
            if (response && response.success && response.data) {
                return response.data.next_token;
            } else {
                // Fallback to local increment
                const number = parseInt(currentToken.replace(/^[A-Z]+/, '')) || 0;
                const nextNumber = number + 1;
                return `${prefix}${String(nextNumber).padStart(3, '0')}`;
            }
        } catch (error) {
            console.error('Error getting next token from API:', error);
            // Fallback to local increment
            const prefix = currentToken.replace(/\d+$/, '');
            const number = parseInt(currentToken.replace(/^[A-Z]+/, '')) || 0;
            const nextNumber = number + 1;
            return `${prefix}${String(nextNumber).padStart(3, '0')}`;
        }
    };

    // Auto-update date and time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date();
            setFieldValue('appointmentDate', now.toISOString().split('T')[0]);
            setFieldValue('appointmentTime', now.toTimeString().slice(0, 5));
        }, 60000);

        return () => clearInterval(interval);
    }, [setFieldValue]);

    // Add new service row
    const addService = () => {
        const newService = {
            id: services.length + 1,
            serviceName: defaultService.name,
            price: defaultService.price
        };
        const updatedServices = [...services, newService];
        setServices(updatedServices);
        setFieldValue('totalAmount', calculateTotal(updatedServices));
    };

    // Update default service settings
    const updateDefaultService = () => {
        const newName = prompt("Enter default service name:", defaultService.name);
        if (newName !== null && newName.trim()) {
            const newPrice = prompt("Enter default price:", defaultService.price);
            if (newPrice !== null && !isNaN(newPrice) && parseFloat(newPrice) >= 0) {
                const updatedDefault = {
                    name: newName.trim(),
                    price: parseFloat(newPrice)
                };
                setDefaultService(updatedDefault);
                
                // Update localStorage
                safeLocalStorage.setItem('defaultService', JSON.stringify(updatedDefault));
                
                // Update current services that match the old default
                const updatedServices = services.map(service => {
                    if (service.serviceName === defaultService.name && service.price === defaultService.price) {
                        return {
                            ...service,
                            serviceName: updatedDefault.name,
                            price: updatedDefault.price
                        };
                    }
                    return service;
                });
                
                setServices(updatedServices);
                const newTotal = calculateTotal(updatedServices);
                setFieldValue('totalAmount', newTotal);
                
                toast.success("Default service updated successfully and applied to current form");
            }
        }
    };

    // Effect to calculate total when services change
    useEffect(() => {
        const total = calculateTotal(services);
        setFieldValue('totalAmount', total);
    }, [services, setFieldValue]);

    // Remove service row
    const removeService = (id) => {
        if (services.length > 1) {
            const updatedServices = services.filter(service => service.id !== id);
            setServices(updatedServices);
            setFieldValue('totalAmount', calculateTotal(updatedServices));
        }
    };

    // Handle service change
    const handleServiceChange = (id, field, value) => {
        const updatedServices = services.map(service => 
            service.id === id ? { ...service, [field]: value } : service
        );
        setServices(updatedServices);
        setFieldValue('totalAmount', calculateTotal(updatedServices));
    };

    const handlePrintReceiptWithData = (receiptData) => {
        const receiptId = `RCP${Date.now()}`;
        
        const printData = {
            clientId: receiptData.patientName.slice(0, 3).toUpperCase() + Math.floor(Math.random() * 1000),
            order: receiptId,
            customerName: receiptData.patientName || 'Patient Name',
            phone: receiptData.phone || '0300-0000000',
            address: receiptData.address || 'Patient Address',
            date: receiptData.appointmentDate || new Date().toISOString(),
            tokenNumber: receiptData.tokenNumber,
            doctorName: receiptData.doctorName,
            deliveryCharges: 0,
            items: receiptData.services?.map(service => ({
                prod_name: service.serviceName,
                quantity: '1',
                prod_price: service.price.toString()
            })) || []
        };

        try {
            safeLocalStorage.setItem('print_data', JSON.stringify(printData));
            window.receiptData = printData;
            
            const printWindow = window.open('/printable3.html', '_blank', 'width=800,height=600');
            
            if (printWindow) {
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.print();
                        printWindow.onafterprint = () => {
                            printWindow.close();
                        };
                    }, 1000);
                };
            } else {
                toast.error('Pop-up blocked. Please allow pop-ups for printing.');
            }
        } catch (error) {
            console.error('Error printing receipt:', error);
            toast.error('Error occurred while printing receipt');
        }
    };

    // Reset token number to a specific value
    const resetTokenNumber = () => {
        const newToken = prompt("Enter new token number (e.g., A001, B001, etc.):", tokenNumber);
        if (newToken && newToken.trim()) {
            const formattedToken = newToken.trim().toUpperCase();
            if (!/^[A-Z]\d{3}$/.test(formattedToken)) {
                toast.error("Invalid token format. Use format like A001, B001, etc.");
                return;
            }
            setTokenNumber(formattedToken);
            safeLocalStorage.setItem('currentTokenNumber', formattedToken);
            toast.success("Token number reset successfully");
        }
    };

    // Handle manual token number change
    const handleTokenChange = (e) => {
        const newToken = e.target.value.toUpperCase();
        setTokenNumber(newToken);
        if (newToken.trim()) {
            safeLocalStorage.setItem('currentTokenNumber', newToken);
        }
    };

    // Rest of your component JSX would go here...
    

    return (
        <>
        <ToastContainer/>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="mb-0">Patient Receipt</h6>
                            {dailyStats && (
                                <div className="daily-stats">
                                    <small className="text-muted">
                                        Today: {dailyStats.total_patients} patients | 
                                        Revenue: Rs. {dailyStats.total_revenue}
                                    </small>
                                </div>
                            )}
                        </div>
                        
                        <form onSubmit={handleSubmit} noValidate>
                            <div className='row'>
                                {/* Token Number Field */}
                                <div className='col-md-6'>
                                    <div className="token-number-container mb-3">
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control token-input"
                                                id="tokenNumber"
                                                value={tokenNumber}
                                                onChange={handleTokenChange}
                                                placeholder="Enter token number"
                                                required
                                            />
                                            {/* <button
                                                type="button"
                                                className="btn btn-outline-secondary reset-btn"
                                                onClick={resetTokenNumber}
                                                title="Reset token number"
                                            >
                                                <i className="fa fa-sync-alt"></i>
                                            </button> */}
                                        </div>
                                        <div className="token-info">
                                            <small className="text-muted">
                                                Current: <span className="current-token">{tokenNumber}</span>
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                {/* Patient Name Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <input
                                            type="text"
                                            className={`form-control ${errors.patientName ? 'is-invalid' : ''}`}
                                            id="patientName"
                                            name="patientName"
                                            value={values.patientName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter patient's name"
                                            required
                                        />
                                        <label htmlFor="patientName">Patient Name *</label>
                                        {errors.patientName && <div className="invalid-feedback">{errors.patientName}</div>}
                                    </div>
                                </div>

                                {/* Phone Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <input
                                            type="tel"
                                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                            id="phone"
                                            name="phone"
                                            value={values.phone}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter phone number"
                                            required
                                        />
                                        <label htmlFor="phone">Phone Number *</label>
                                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                    </div>
                                </div>

                                {/* Doctor Name Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <select
                                            className={`form-select ${errors.doctorName ? 'is-invalid' : ''}`}
                                            id="doctorName"
                                            name="doctorName"
                                            value={values.doctorName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        >
                                            <option value="">Select Doctor</option>
                                            <option value="Muhammad Zubair Mukhtar">Dr. Zubair Mukhtar</option>
                                            <option value="Dr. Haris Burki">Dr. Haris Burki</option>
                                        </select>
                                        <label htmlFor="doctorName">Doctor Name</label>
                                        {errors.doctorName && <div className="invalid-feedback">{errors.doctorName}</div>}
                                    </div>
                                </div>

                                {/* Address Field */}
                                <div className='col-md-6'>
                                    <div className="form-floating mb-3">
                                        <textarea
                                            className="form-control"
                                            id="address"
                                            name="address"
                                            value={values.address}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter patient's address"
                                            style={{ height: '58px' }}
                                        />
                                        <label htmlFor="address">Address</label>
                                    </div>
                                </div>

                                {/* Services Section */}
                                <div className='col-md-6'>
                                    <div className="services-section mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <label className="form-label">Services</label>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={addService}
                                                >
                                                    <i className="fa fa-plus"></i> Add Service
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={updateDefaultService}
                                                >
                                                    <i className="fa fa-cog"></i> Default
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {services.map((service, index) => (
                                            <div key={service.id} className="service-row mb-2">
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Service name"
                                                        value={service.serviceName}
                                                        onChange={(e) => handleServiceChange(service.id, 'serviceName', e.target.value)}
                                                    />
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        placeholder="Price"
                                                        value={service.price}
                                                        onChange={(e) => handleServiceChange(service.id, 'price', parseFloat(e.target.value) || 0)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {services.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger"
                                                            onClick={() => removeService(service.id)}
                                                        >
                                                            <i className="fa fa-times"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        
                                        <div className="total-amount mt-3">
                                            <strong>Total: Rs. {values.totalAmount}</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Auto Date and Time Display */}
                                <div className='col-md-12 mb-3'>
                                    <div className="alert alert-info">
                                        <strong>Appointment:</strong> {new Date(values.appointmentDate + 'T' + values.appointmentTime).toLocaleString()}
                                    </div>
                                </div>

                                {/* Submit Buttons */}
                                <div className="col-md-12 d-flex gap-2">
                                    <button
                                        className="btn btn-primary"
                                        type="submit"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Creating..." : "Create & Print Receipt"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                            const resetServices = [{ id: 1, serviceName: defaultService.name, price: defaultService.price }];
                                            resetForm({
                                                values: {
                                                    patientName: '',
                                                    doctorName: '',
                                                    appointmentDate: new Date().toISOString().split('T')[0],
                                                    appointmentTime: new Date().toTimeString().slice(0, 5),
                                                    phone: '',
                                                    address: '',
                                                    totalAmount: calculateTotal(resetServices)
                                                }
                                            });
                                            setServices(resetServices);
                                        }}
                                    >
                                        Reset Form
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <Footer />
            </div>

            <style jsx>{`
                .btn-submit {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    padding: 14px 28px;
                    font-size: 16px;
                    font-weight: 600;
                    text-transform: uppercase;
                    border-radius: 30px;
                    transition: background-color 0.3s, border-color 0.3s;
                    display: inline-block;
                    margin-top: 10px;
                    color: #fff;
                }

                .btn-submit:hover {
                    background-color: #d43f00;
                    border-color: #d43f00;
                    color: #fff;
                }

                .btn-submit:focus {
                    outline: none;
                }

                .service-row {
                    background-color: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                    border: 1px solid #dee2e6;
                }

                .invalid-feedback {
                    display: block;
                }

                .btn-outline-primary:hover {
                    background-color: #f44c00;
                    border-color: #f44c00;
                }

                .btn-outline-info:hover {
                    background-color: #17a2b8;
                    border-color: #17a2b8;
                    color: #fff;
                }

                .default-service-info {
                    background-color: #e8f4f8;
                    padding: 8px 12px;
                    border-radius: 6px;
                    border-left: 4px solid #17a2b8;
                }

                .default-service-info i {
                    color: #17a2b8;
                }

                .alert-info {
                    background-color: #d1ecf1;
                    border-color: #bee5eb;
                    color: #0c5460;
                    padding: 10px;
                    border-radius: 5px;
                    font-size: 14px;
                }

                #tokenNumber {
                    font-size: 16px !important;
                    font-weight: bold !important;
                    color: #d43f00 !important;
                }

                .form-text {
                    font-size: 11px;
                    margin-top: 5px;
                }

                .token-number-container {
                    position: relative;
                }
                
                .token-number-container .form-label {
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 8px;
                    font-size: 14px;
                }
                
                .token-input {
                    font-size: 18px !important;
                    font-weight: bold !important;
                    color: #d43f00 !important;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    border: 2px solid #dee2e6;
                    border-radius: 8px 0 0 8px !important;
                    padding: 12px 16px;
                    height: 50px;
                    background-color: #fff;
                    transition: all 0.3s ease;
                }
                
                .token-input:focus {
                    border-color: #f44c00 !important;
                    box-shadow: 0 0 0 0.2rem rgba(244, 76, 0, 0.25) !important;
                    background-color: #fff;
                    color: #d43f00 !important;
                }
                
                .token-input::placeholder {
                    color: #adb5bd;
                    font-weight: normal;
                    font-size: 14px;
                    text-transform: none;
                    letter-spacing: normal;
                }
                
                .reset-btn {
                    border: 2px solid #dee2e6;
                    border-left: none;
                    border-radius: 0 8px 8px 0 !important;
                    padding: 12px 16px;
                    height: 50px;
                    background-color: #f8f9fa;
                    color: #6c757d;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .reset-btn:hover {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    color: #fff;
                    transform: translateY(-1px);
                }
                
                .reset-btn:focus {
                    box-shadow: 0 0 0 0.2rem rgba(244, 76, 0, 0.25);
                    border-color: #f44c00;
                }
                
                .token-info {
                    margin-top: 8px;
                    padding: 8px 12px;
                    background-color: #f8f9fa;
                    border-radius: 6px;
                    border-left: 4px solid #f44c00;
                }
                
                .token-info small {
                    font-size: 12px;
                    line-height: 1.4;
                }
                
                .current-token {
                    font-weight: bold;
                    color: #f44c00;
                    background-color: #fff;
                    padding: 2px 6px;
                    border-radius: 4px;
                    border: 1px solid #f44c00;
                }
                
                .next-token {
                    font-weight: bold;
                    color: #28a745;
                    background-color: #fff;
                    padding: 2px 6px;
                    border-radius: 4px;
                    border: 1px solid #28a745;
                }
                
                /* Input group focus state */
                .input-group:focus-within .token-input {
                    border-color: #f44c00;
                    z-index: 3;
                }
                
                .input-group:focus-within .reset-btn {
                    border-color: #f44c00;
                    z-index: 3;
                }

                /* Doctor Select Dropdown Styling */
                .form-select {
                    font-size: 16px;
                    font-weight: 500;
                    color: #495057;
                    border: 1px solid #ced4da;
                    border-radius: 0.375rem;
                    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                }

                .form-select:focus {
                    border-color: #f44c00;
                    box-shadow: 0 0 0 0.2rem rgba(244, 76, 0, 0.25);
                }

                .form-select option {
                    padding: 8px 12px;
                    font-size: 14px;
                }

                .form-select option:first-child {
                    color: #6c757d;
                    font-style: italic;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .token-input {
                        font-size: 16px !important;
                        height: 48px;
                        padding: 10px 14px;
                    }
                    
                    .reset-btn {
                        height: 48px;
                        padding: 10px 14px;
                    }
                    
                    .token-info {
                        padding: 6px 10px;
                    }
                    
                    .token-info small {
                        font-size: 11px;
                    }

                    .form-select {
                        font-size: 14px;
                    }
                }
                
                /* Animation for token change */
                .current-token, .next-token {
                    transition: all 0.3s ease;
                }
                
                .current-token:hover, .next-token:hover {
                    transform: scale(1.05);
                }
            `}</style>
        </>
    );
}